import React, { useCallback, useEffect } from "react";
import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Underline as UnderlineIcon } from "lucide-react";

import { isEmptyComment } from "./commentUtils";
import CommentAvatar from "./CommentAvatar";

function FormatButton({ active, onClick, title, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`rounded p-1.5 transition-colors ${
        active
          ? "bg-gray-700 text-white"
          : "text-gray-600 hover:bg-gray-200 hover:text-gray-900"
      }`}
    >
      {children}
    </button>
  );
}

export default function RichCommentEditor({
  profile,
  placeholder = "Comments",
  onSubmit,
  submitLabel = "Add Comment",
  inputBgClass = "bg-[#EEF0ED]",
  useGradientButton = false,
}) {
  const authorName = profile?.displayName || "";
  const avatarSrc = profile?.avatar;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        underline: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
      }),
      Underline,
      Placeholder.configure({ placeholder }),
    ],
    editorProps: {
      attributes: {
        class: "comment-editor-prose",
      },
    },
  });

  useEffect(() => {
    return () => {
      editor?.destroy();
    };
  }, [editor]);

  const editorState = useEditorState({
    editor,
    selector: (ctx) => ({
      html: ctx.editor ? ctx.editor.getHTML() : "",
      isBold: ctx.editor ? ctx.editor.isActive("bold") : false,
      isItalic: ctx.editor ? ctx.editor.isActive("italic") : false,
      isUnderline: ctx.editor ? ctx.editor.isActive("underline") : false,
    }),
  });

  const handleSubmit = useCallback(async () => {
    if (!editor) return;
    const html = editor.getHTML();
    if (isEmptyComment(html)) return;

    await onSubmit(html);
    editor.commands.clearContent();
  }, [editor, onSubmit]);

  if (!editor) return null;

  const hasContent = !isEmptyComment(editorState.html);

  return (
    <div className="mb-6">
      <div className="flex gap-3">
        <CommentAvatar
          name={authorName}
          src={avatarSrc}
          size={32}
        />
        <div className="min-w-0 flex-1">
          <div
            className={`comment-rich-editor rounded text-sm text-black ${inputBgClass}`}
          >
            <div className="flex items-center gap-0.5 border-b border-gray-300/60 px-2 py-1">
              <FormatButton
                title="Bold"
                active={editorState.isBold}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <Bold size={16} />
              </FormatButton>
              <FormatButton
                title="Italic"
                active={editorState.isItalic}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <Italic size={16} />
              </FormatButton>
              <FormatButton
                title="Underline"
                active={editorState.isUnderline}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
              >
                <UnderlineIcon size={16} />
              </FormatButton>
            </div>
            <EditorContent editor={editor} />
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              disabled={!hasContent}
              onClick={handleSubmit}
              className={
                useGradientButton
                  ? `inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ease-out shadow-sm ${
                      hasContent
                        ? "bg-gradient-to-r from-gray-900 to-gray-700 text-white hover:shadow-md hover:-translate-y-[1px] active:translate-y-0"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`
                  : `bg-[#3b3b3b] text-white py-2 px-4 text-sm rounded hover:bg-opacity-70 ${
                      !hasContent ? "opacity-50 cursor-not-allowed" : ""
                    }`
              }
            >
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
